import {
  type BackfillBlockedReason,
  type BackfillRequestStatus,
  type DeliveryStatus,
  type OrderStatus,
  TimeSlot as TimeSlotSchema,
  type TimeSlot,
} from "./enums";

/**
 * ラベルマップから表示名を引く。未知値は例外にせず生値をそのまま返す。
 * enum に値が増えたときに画面が落ちるのを避けつつ、生値が出ることで調査可能にする
 * (ラベル側の網羅漏れは `Record<Enum, string>` の型と網羅性テストで検出する)。
 */
export function labelOf<K extends string>(map: Record<K, string>, value: string): string {
  return (map as Record<string, string | undefined>)[value] ?? value;
}

/**
 * 配送ステータス (route_stops.status) の表示ラベル (単一ソース)。
 * driver の配送履歴・admin の配送状況/監査パネル/運転日報 CSV で共用する。
 */
export const DELIVERY_STATUS_LABELS: Record<DeliveryStatus, string> = {
  pending: "未配送",
  in_transit: "配送中",
  arrived: "到着",
  completed: "完了",
  skipped: "スキップ",
  cancelled: "キャンセル",
};

/** 配送ステータスを日本語表示に変換する。未知値はそのまま返す (fail-safe)。 */
export const deliveryStatusLabel = (status: string): string =>
  labelOf(DELIVERY_STATUS_LABELS, status);

const TIME_SLOT_LABEL: Record<TimeSlot, string> = {
  early_morning: "早朝便",
  am: "午前便",
  pm: "午後便",
};

const TIME_SLOT_SHORT_LABEL: Record<TimeSlot, string> = {
  early_morning: "早朝",
  am: "午前",
  pm: "午後",
};

export type TimeSlotLabelInput = TimeSlot | Uppercase<TimeSlot> | string;

export function normalizeTimeSlotLabelInput(slot: TimeSlotLabelInput): TimeSlot | null {
  const parsed = TimeSlotSchema.safeParse(slot.trim().toLowerCase());
  return parsed.success ? parsed.data : null;
}

export const timeSlotLabel = (slot: TimeSlotLabelInput): string => {
  const normalized = normalizeTimeSlotLabelInput(slot);
  return normalized ? TIME_SLOT_LABEL[normalized] : "不明";
};

export const timeSlotShortLabel = (slot: TimeSlotLabelInput): string => {
  const normalized = normalizeTimeSlotLabelInput(slot);
  return normalized ? TIME_SLOT_SHORT_LABEL[normalized] : "不明";
};

export const TIME_SLOT_OPTIONS: Array<{ value: TimeSlot; label: string }> = [
  { value: "early_morning", label: TIME_SLOT_LABEL.early_morning },
  { value: "am", label: TIME_SLOT_LABEL.am },
  { value: "pm", label: TIME_SLOT_LABEL.pm },
];

export const TIME_SLOT_SHORT_OPTIONS: Array<{ value: TimeSlot; label: string }> = [
  { value: "early_morning", label: TIME_SLOT_SHORT_LABEL.early_morning },
  { value: "am", label: TIME_SLOT_SHORT_LABEL.am },
  { value: "pm", label: TIME_SLOT_SHORT_LABEL.pm },
];

/**
 * 注文ステータスの表示ラベル (#739)。注文一覧・受注詳細・配車画面 (注文詳細モーダル) で共用する。
 * 画面ごとにローカルの対応表を作ると、新しい表示箇所でだけ変換が漏れて enum の生値が出るため、
 * 日本語表記はここを単一ソースにする。
 */
export const ORDER_STATUS_LABELS = {
  draft: "下書き",
  confirmed: "確定",
  assigned: "配車済",
  completed: "完了",
  cancelled: "キャンセル",
} as const satisfies Record<OrderStatus, string>;

/** 注文ステータスを日本語表示に変換する。未知値は例外にせずそのまま返す (調査可能性を残す)。 */
export const orderStatusLabel = (status: string): string => labelOf(ORDER_STATUS_LABELS, status);

/**
 * #769: 後追い登録の申請状態の表示ラベル。driver アプリ・admin 画面・API のエラー文言で共用する。
 * 画面ごとにローカルの対応表を作ると新しい表示箇所でだけ生値が出るため、ここを単一ソースにする。
 */
export const BACKFILL_REQUEST_STATUS_LABELS = {
  pending: "承認待ち",
  approved: "承認済",
  rejected: "却下",
  cancelled: "取下げ",
} as const satisfies Record<BackfillRequestStatus, string>;

/** 後追い登録の申請状態を日本語表示に変換する。未知値はそのまま返す (調査可能性を残す)。 */
export const backfillRequestStatusLabel = (status: string): string =>
  labelOf(BACKFILL_REQUEST_STATUS_LABELS, status);

/**
 * #769: 後追い登録を拒否する理由の文言。API のエラーメッセージと driver/admin の案内で共用する。
 * サーバが返すのは理由コードだけで、画面は判定を持たずこの表だけを引く。
 */
export const BACKFILL_BLOCKED_MESSAGES = {
  not_past_date: "当日・未来日の実績は通常の打刻から登録してください",
  already_finalized: "この配送先は既に完了・スキップ・キャンセル済みです",
  route_editing: "配車担当がレーンを編集中です。確定後に登録してください",
  report_locked: "承認済の運転日報に含まれた日の実績は登録できません",
  invoice_locked: "発行済の請求書に含まれた月の実績は登録できません",
  time_out_of_work_date: "対象日の時刻を入力してください",
  arrival_after_departure: "到着時刻は完了時刻より前にしてください",
} as const satisfies Record<BackfillBlockedReason, string>;

/** 後追い登録の拒否理由を操作者向けの日本語にする。未知値はそのまま返す。 */
export const backfillBlockedMessage = (reason: string): string =>
  labelOf(BACKFILL_BLOCKED_MESSAGES, reason);
